import type { JSX } from 'react';
import { useEffect } from 'react';
import { failureFrom } from '@/lib/result';
import { card, segment } from '../shell/look';
import { PageHead } from '../shell/page-head';
import type { Route } from '../shell/routes';
import { EmptyState, ErrorState, LoadingState } from '../shell/states';
import { navigate, replaceRoute } from '../shell/use-route';
import { ActivityPager } from './activity-pager';
import { activityRoute, pageCount, readActivityView } from './activity-query';
import type { ActivityView } from './activity-query';
import { ActivityRow } from './activity-row';
import { FILTERS } from './activity-types';
import { useActivityPage } from './use-activity';

/**
 * The audit trail (design L431–455). Filter and page live in the URL —
 * `#/activity?type=error&page=2` — so a reload or a shared link lands on the
 * same rows.
 */
export function ActivityPage({ route }: { route: Route }): JSX.Element {
  const view = readActivityView(route);
  return (
    <section className="animate-rise">
      <PageHead eyebrow="AUDIT TRAIL" title="Activity log" action={<Filters view={view} />} />
      <Feed view={view} />
    </section>
  );
}

function Filters({ view }: { view: ActivityView }): JSX.Element {
  return (
    <div className={segment.group} role="tablist" aria-label="Filter activity">
      {FILTERS.map((filter) => {
        const on = filter.type === view.type;
        return (
          <button
            key={filter.label}
            type="button"
            role="tab"
            aria-selected={on}
            className={on ? segment.on : segment.off}
            // A new filter starts from its first page.
            onClick={() => navigate(activityRoute({ type: filter.type, page: 1 }))}
          >
            {filter.label}
          </button>
        );
      })}
    </div>
  );
}

function Feed({ view }: { view: ActivityView }): JSX.Element {
  const feed = useActivityPage(view);
  const total = feed.data?.total;

  // A page past the end — rows aged out, or an edited link — goes to the last one.
  useEffect(() => {
    if (total === undefined || total === 0) return;
    const last = pageCount(total);
    if (view.page > last) replaceRoute(activityRoute({ ...view, page: last }));
  }, [total, view]);

  if (feed.isPending) return <LoadingState rows={10} />;
  if (feed.isError) {
    const { status, detail } = failureFrom(feed.error);
    return <ErrorState status={status} detail={detail} onRetry={() => void feed.refetch()} />;
  }

  const { items } = feed.data;
  return (
    <>
      <div className={`${card} overflow-x-auto ${feed.isPlaceholderData ? 'opacity-60' : ''}`}>
        {items.length === 0 ? (
          <EmptyState
            title="Nothing here yet"
            body="Replies, syncs, blocked senders and errors show up as they happen."
          />
        ) : (
          items.map((item) => <ActivityRow key={item.id} item={item} />)
        )}
      </div>
      <ActivityPager
        page={view.page}
        total={total ?? items.length}
        onPage={(page) => {
          navigate(activityRoute({ ...view, page }));
          document.querySelector('main')?.scrollTo({ top: 0 });
        }}
      />
    </>
  );
}
